import { useState, useMemo } from 'react';
import { Calculator, DollarSign, Percent } from 'lucide-react';

const money = (n) => (isFinite(n) ? n.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }) : '—');
const pct = (n) => (isFinite(n) ? `${n.toFixed(2)}%` : '—');

/**
 * Three long-term investor calculators. Client-side math only.
 * Every default is set to Southern Ontario 2026 medians; overwrite freely.
 */
export default function InvestorCalculators() {
    const [tab, setTab] = useState('cash-flow');
    return (
        <div className="border border-neutral-200 bg-white" data-testid="investor-calculators">
            <div className="flex border-b border-neutral-200 text-[13px] font-mono-data tracking-wider uppercase">
                {[
                    { k: 'cash-flow', l: 'Cash Flow', icon: <DollarSign className="w-3.5 h-3.5" /> },
                    { k: 'roi', l: 'ROI', icon: <Calculator className="w-3.5 h-3.5" /> },
                    { k: 'cap-rate', l: 'Cap Rate', icon: <Percent className="w-3.5 h-3.5" /> },
                ].map((t) => (
                    <button
                        key={t.k}
                        onClick={() => setTab(t.k)}
                        data-testid={`calc-tab-${t.k}`}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-4 border-r border-neutral-200 last:border-r-0 transition-colors ${
                            tab === t.k ? 'bg-brand-navy text-white' : 'text-neutral-500 hover:text-black hover:bg-[#FAFAF8]'
                        }`}
                    >
                        {t.icon}
                        {t.l}
                    </button>
                ))}
            </div>
            <div className="p-6 lg:p-8">
                {tab === 'cash-flow' && <CashFlowCalculator />}
                {tab === 'roi' && <ROICalculator />}
                {tab === 'cap-rate' && <CapRateCalculator />}
            </div>
        </div>
    );
}

// =============================================================================
// CASH FLOW
// =============================================================================
function CashFlowCalculator() {
    const [f, setF] = useState({
        monthly_rent: 2450,
        vacancy_pct: 6,
        property_management_pct: 8,
        mortgage_payment: 3100,
        property_tax_annual: 5800,
        insurance_annual: 1450,
        maintenance_annual: 3600,
        utilities_annual: 0,
        other_annual: 0,
    });
    const set = (k) => (e) => setF({ ...f, [k]: Number(e.target.value) || 0 });

    const calc = useMemo(() => {
        const gross_annual = f.monthly_rent * 12;
        const vacancy_loss = gross_annual * (f.vacancy_pct / 100);
        const effective_rent = gross_annual - vacancy_loss;
        const mgmt = effective_rent * (f.property_management_pct / 100);
        const opex = f.property_tax_annual + f.insurance_annual + f.maintenance_annual + f.utilities_annual + f.other_annual + mgmt;
        const noi = effective_rent - opex;
        const annual_debt = f.mortgage_payment * 12;
        const cash_flow_annual = noi - annual_debt;
        const cash_flow_monthly = cash_flow_annual / 12;
        return { gross_annual, vacancy_loss, effective_rent, mgmt, opex, noi, annual_debt, cash_flow_annual, cash_flow_monthly };
    }, [f]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3 grid grid-cols-2 gap-4">
                <Input label="Monthly rent" v={f.monthly_rent} onChange={set('monthly_rent')} prefix="$" testid="cf-rent" />
                <Input label="Vacancy %" v={f.vacancy_pct} onChange={set('vacancy_pct')} suffix="%" testid="cf-vacancy" />
                <Input label="Property management %" v={f.property_management_pct} onChange={set('property_management_pct')} suffix="%" testid="cf-mgmt" />
                <Input label="Monthly mortgage payment" v={f.mortgage_payment} onChange={set('mortgage_payment')} prefix="$" testid="cf-mortgage" />
                <Input label="Property tax (annual)" v={f.property_tax_annual} onChange={set('property_tax_annual')} prefix="$" testid="cf-tax" />
                <Input label="Insurance (annual)" v={f.insurance_annual} onChange={set('insurance_annual')} prefix="$" testid="cf-ins" />
                <Input label="Maintenance (annual)" v={f.maintenance_annual} onChange={set('maintenance_annual')} prefix="$" testid="cf-maint" />
                <Input label="Utilities (if paid)" v={f.utilities_annual} onChange={set('utilities_annual')} prefix="$" />
                <Input label="Other (annual)" v={f.other_annual} onChange={set('other_annual')} prefix="$" className="col-span-2" />
            </div>
            <div className="lg:col-span-2 space-y-3" data-testid="cf-results">
                <ResultRow k="Gross annual rent" v={money(calc.gross_annual)} />
                <ResultRow k="− Vacancy" v={money(-calc.vacancy_loss)} />
                <ResultRow k="− Operating expenses" v={money(-calc.opex)} />
                <ResultRow k="Net Operating Income" v={money(calc.noi)} strong />
                <ResultRow k="− Annual debt service" v={money(-calc.annual_debt)} />
                <div className="border-t border-neutral-300 pt-4 mt-2">
                    <ResultRow k="Annual cash flow" v={money(calc.cash_flow_annual)} big />
                    <ResultRow k="Monthly cash flow" v={money(calc.cash_flow_monthly)} big />
                </div>
                <p className="text-[11.5px] text-neutral-500 leading-relaxed pt-3">
                    Long-term investor thumb rule: aim for positive cash flow at your <em>stress-tested</em> mortgage
                    rate (typically Bank of Canada policy + 1.5%). If it only cash-flows at today&apos;s rate, it&apos;s
                    a rate bet, not an income asset.
                </p>
            </div>
        </div>
    );
}

// =============================================================================
// ROI
// =============================================================================
function ROICalculator() {
    const [f, setF] = useState({
        purchase_price: 725000,
        down_payment_pct: 20,
        closing_costs: 22000,
        renovations_upfront: 8000,
        monthly_cash_flow: 250,
        annual_appreciation_pct: 3.5,
        annual_principal_paydown: 8400,
        hold_years: 10,
    });
    const set = (k) => (e) => setF({ ...f, [k]: Number(e.target.value) || 0 });

    const calc = useMemo(() => {
        const down = f.purchase_price * (f.down_payment_pct / 100);
        const total_capital = down + f.closing_costs + f.renovations_upfront;
        const annual_cash = f.monthly_cash_flow * 12;
        // Total appreciation over hold using compound growth on purchase price
        const future_value = f.purchase_price * Math.pow(1 + f.annual_appreciation_pct / 100, f.hold_years);
        const total_appreciation = future_value - f.purchase_price;
        const total_principal = f.annual_principal_paydown * f.hold_years;
        const total_cash_flow = annual_cash * f.hold_years;
        const total_return = total_appreciation + total_principal + total_cash_flow;
        const annualised = total_capital > 0 && f.hold_years > 0
            ? (Math.pow((total_capital + total_return) / total_capital, 1 / f.hold_years) - 1) * 100
            : 0;
        const cash_on_cash_year1 = total_capital > 0 ? (annual_cash / total_capital) * 100 : 0;
        return { down, total_capital, annual_cash, future_value, total_appreciation, total_principal, total_cash_flow, total_return, annualised, cash_on_cash_year1 };
    }, [f]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3 grid grid-cols-2 gap-4">
                <Input label="Purchase price" v={f.purchase_price} onChange={set('purchase_price')} prefix="$" testid="roi-price" />
                <Input label="Down payment %" v={f.down_payment_pct} onChange={set('down_payment_pct')} suffix="%" testid="roi-down" />
                <Input label="Closing costs" v={f.closing_costs} onChange={set('closing_costs')} prefix="$" />
                <Input label="Upfront renovations" v={f.renovations_upfront} onChange={set('renovations_upfront')} prefix="$" />
                <Input label="Monthly cash flow" v={f.monthly_cash_flow} onChange={set('monthly_cash_flow')} prefix="$" testid="roi-cashflow" />
                <Input label="Annual appreciation %" v={f.annual_appreciation_pct} onChange={set('annual_appreciation_pct')} suffix="%" testid="roi-app" />
                <Input label="Annual principal paydown" v={f.annual_principal_paydown} onChange={set('annual_principal_paydown')} prefix="$" />
                <Input label="Hold years" v={f.hold_years} onChange={set('hold_years')} suffix="yr" testid="roi-years" />
            </div>
            <div className="lg:col-span-2 space-y-3" data-testid="roi-results">
                <ResultRow k="Total capital invested" v={money(calc.total_capital)} />
                <ResultRow k="Future property value" v={money(calc.future_value)} />
                <ResultRow k="Appreciation over hold" v={money(calc.total_appreciation)} />
                <ResultRow k="Principal paid down" v={money(calc.total_principal)} />
                <ResultRow k="Cash flow over hold" v={money(calc.total_cash_flow)} />
                <div className="border-t border-neutral-300 pt-4 mt-2">
                    <ResultRow k="Cash-on-cash yr 1" v={pct(calc.cash_on_cash_year1)} big />
                    <ResultRow k="Annualised total return" v={pct(calc.annualised)} big />
                </div>
                <p className="text-[11.5px] text-neutral-500 leading-relaxed pt-3">
                    Annualised total return includes cash flow, principal paydown, and appreciation. Long-term
                    investor benchmark: 8-12% annualised on stress-tested assumptions is a real deal in
                    Southern Ontario. Anything above 15% either uses aggressive appreciation numbers or
                    optimistic operating costs — re-check the inputs.
                </p>
            </div>
        </div>
    );
}

// =============================================================================
// CAP RATE
// =============================================================================
function CapRateCalculator() {
    const [f, setF] = useState({
        property_value: 725000,
        annual_rent: 29400,
        vacancy_pct: 6,
        property_tax_annual: 5800,
        insurance_annual: 1450,
        maintenance_annual: 3600,
        management_pct: 8,
        utilities_annual: 0,
        other_annual: 0,
    });
    const set = (k) => (e) => setF({ ...f, [k]: Number(e.target.value) || 0 });

    const calc = useMemo(() => {
        const vacancy_loss = f.annual_rent * (f.vacancy_pct / 100);
        const effective_rent = f.annual_rent - vacancy_loss;
        const mgmt = effective_rent * (f.management_pct / 100);
        const opex = f.property_tax_annual + f.insurance_annual + f.maintenance_annual + f.utilities_annual + f.other_annual + mgmt;
        const noi = effective_rent - opex;
        const cap_rate = f.property_value > 0 ? (noi / f.property_value) * 100 : 0;
        return { effective_rent, opex, noi, cap_rate };
    }, [f]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3 grid grid-cols-2 gap-4">
                <Input label="Property value" v={f.property_value} onChange={set('property_value')} prefix="$" testid="cr-value" />
                <Input label="Annual rent" v={f.annual_rent} onChange={set('annual_rent')} prefix="$" testid="cr-rent" />
                <Input label="Vacancy %" v={f.vacancy_pct} onChange={set('vacancy_pct')} suffix="%" />
                <Input label="Management %" v={f.management_pct} onChange={set('management_pct')} suffix="%" />
                <Input label="Property tax (annual)" v={f.property_tax_annual} onChange={set('property_tax_annual')} prefix="$" />
                <Input label="Insurance (annual)" v={f.insurance_annual} onChange={set('insurance_annual')} prefix="$" />
                <Input label="Maintenance (annual)" v={f.maintenance_annual} onChange={set('maintenance_annual')} prefix="$" />
                <Input label="Utilities" v={f.utilities_annual} onChange={set('utilities_annual')} prefix="$" />
                <Input label="Other (annual)" v={f.other_annual} onChange={set('other_annual')} prefix="$" className="col-span-2" />
            </div>
            <div className="lg:col-span-2 space-y-3" data-testid="cr-results">
                <ResultRow k="Effective rent (after vacancy)" v={money(calc.effective_rent)} />
                <ResultRow k="Operating expenses" v={money(calc.opex)} />
                <ResultRow k="Net Operating Income" v={money(calc.noi)} strong />
                <div className="border-t border-neutral-300 pt-4 mt-2">
                    <ResultRow k="Cap rate" v={pct(calc.cap_rate)} big />
                </div>
                <p className="text-[11.5px] text-neutral-500 leading-relaxed pt-3">
                    Southern Ontario 2026 cap-rate norms: 3.5-4.5% in Halton (appreciation market),
                    4.0-5.0% in Durham and Niagara, 4.5-5.5% in Hamilton and Brantford (yield markets).
                    Below 3% signals overpayment; above 6% signals hidden operational risk. Read the deal.
                </p>
            </div>
        </div>
    );
}

// =============================================================================
// primitives
// =============================================================================
function Input({ label, v, onChange, prefix, suffix, className, testid }) {
    return (
        <label className={`block ${className || ''}`}>
            <div className="text-[10.5px] tracking-[0.2em] uppercase text-neutral-500 font-mono-data mb-1.5">
                {label}
            </div>
            <div className="flex items-stretch border border-neutral-300 focus-within:border-black transition-colors">
                {prefix && <span className="px-2.5 py-2 text-[13px] text-neutral-500 font-mono-data border-r border-neutral-200 bg-[#FAFAF8]">{prefix}</span>}
                <input
                    type="number"
                    value={v}
                    onChange={onChange}
                    data-testid={testid}
                    className="flex-1 min-w-0 px-3 py-2 text-[14px] font-mono-data tabular focus:outline-none"
                />
                {suffix && <span className="px-2.5 py-2 text-[13px] text-neutral-500 font-mono-data border-l border-neutral-200 bg-[#FAFAF8]">{suffix}</span>}
            </div>
        </label>
    );
}

function ResultRow({ k, v, strong, big }) {
    return (
        <div className="flex items-baseline justify-between gap-3">
            <div className={`text-[13px] ${strong ? 'text-black font-medium' : 'text-neutral-600'}`}>{k}</div>
            <div className={`font-mono-data tabular ${big ? 'text-[22px] font-medium text-black' : strong ? 'text-[16px] text-black' : 'text-[14px] text-neutral-800'}`}>
                {v}
            </div>
        </div>
    );
}
